import { getNavioBuildOfflineCapability } from './offline-capabilities';

import type {
  NavioBuildConflictPolicy,
  NavioBuildOfflineCapability,
  NavioBuildOfflineEntity,
  NavioBuildQueuedWriteRisk,
} from '../../../../../packages/shared/src/types/naviobuild-offline';


export type OfflineWriteMode = 'online' | 'queue' | 'queue_with_review' | 'block';

export interface OfflineWriteDecision {
  mode: OfflineWriteMode;
  routeId: string;
  entity: NavioBuildOfflineEntity;
  conflictPolicy: NavioBuildConflictPolicy;
  queuedWriteRisk: NavioBuildQueuedWriteRisk;
  reason: 'online' | 'offline_supported' | 'offline_requires_review' | 'offline_blocked';
  capability: NavioBuildOfflineCapability | null;
}

export interface OfflineWriteDecisionInput {
  routeId: string;
  entity: NavioBuildOfflineEntity;
  isOnline: boolean;
}

function buildDecision({
  mode,
  reason,
  routeId,
  entity,
  capability,
}: Pick<OfflineWriteDecision, 'mode' | 'reason' | 'routeId' | 'entity' | 'capability'>): OfflineWriteDecision {
  return {
    mode,
    reason,
    routeId,
    entity,
    capability,
    conflictPolicy: capability?.conflictPolicy ?? 'blocked_offline',
    queuedWriteRisk: capability?.queuedWriteRisk ?? 'blocked_offline',
  };
}

function requiresReview(capability: NavioBuildOfflineCapability): boolean {
  return (
    capability.conflictPolicy === 'human_review_required' ||
    capability.conflictPolicy === 'server_authoritative' ||
    capability.queuedWriteRisk === 'high'
  );
}

function isBlocked(capability: NavioBuildOfflineCapability): boolean {
  return (
    capability.conflictPolicy === 'blocked_offline' ||
    capability.queuedWriteRisk === 'blocked_offline'
  );
}

export function decideNavioBuildOfflineWrite({
  routeId,
  entity,
  isOnline,
}: OfflineWriteDecisionInput): OfflineWriteDecision {
  const capability = getNavioBuildOfflineCapability(routeId) ?? null;

  if (!capability || !capability.entities.includes(entity)) {
    return buildDecision({
      mode: isOnline ? 'online' : 'block',
      routeId,
      entity,
      reason: isOnline ? 'online' : 'offline_blocked',
      capability,
    });
  }

  if (isOnline) {
    return buildDecision({
      mode: 'online',
      routeId,
      entity,
      reason: 'online',
      capability,
    });
  }

  if (isBlocked(capability)) {
    return buildDecision({
      mode: 'block',
      routeId,
      entity,
      reason: 'offline_blocked',
      capability,
    });
  }

  if (requiresReview(capability)) {
    return buildDecision({
      mode: 'queue_with_review',
      routeId,
      entity,
      reason: 'offline_requires_review',
      capability,
    });
  }

  return buildDecision({
    mode: 'queue',
    routeId,
    entity,
    reason: 'offline_supported',
    capability,
  });
}
